/**
 * HERO WAVES CONTROLLER
 * ====================
 *
 * Manages the canvas lifecycle for the hero background:
 * - creates and sizes the canvas
 * - handles resize, visibility, and performance throttling
 * - drives the animation loop (requestAnimationFrame)
 *
 * This file does NOT define visuals.
 * Visual effects (ribbons, waves, particles, etc.) are implemented
 * as isolated modules (e.g. heroRibbons.js) and plugged in here.
 *
 * WHY SPLIT THIS WAY?
 * ------------------
 * - Keeps animation math separate from DOM / lifecycle logic
 * - Makes it easy to add or swap effects later:
 *     - ribbons
 *     - sine waves
 *     - particles
 *     - noise / grain overlays
 *
 * heroWaves.js = orchestration
 * heroRibbons.js = visuals
 */
import { createCanvasTicker } from "../utils/canvasTicker.js";
import { createHeroRibbons } from "./heroRibbons.js";

export function initHeroWaves({ onReady } = {}) {
  const hero = document.querySelector(".hero");
  const waveBg = document.querySelector(".hero__wave-bg");
  if (!waveBg) return;

  const canvas = document.createElement("canvas");
  waveBg.appendChild(canvas);

  const ctx = canvas.getContext("2d", { alpha: true });

  let width = 0,
    height = 0,
    dpr = 1;

  let ribbons = null;
  let readySent = false;

  function setupLayers() {
    const isFirefox = /firefox/i.test(navigator.userAgent);
    ribbons = createHeroRibbons(ctx, width, height, {
      count: 3,
      trail: 110,
      step: 2,
      highlightEvery: 3,
      thicknessMin: 12,
      thicknessMax: 28,
      baseAlpha: isFirefox ? 0.07 : 0.045,
      highlightAlpha: isFirefox ? 0.03 : 0.018,
      amplitude: 0.65,
      composite: isFirefox ? "screen" : "lighter",
    });
  }

  function resize() {
    const rect = waveBg.getBoundingClientRect();
    const newW = Math.max(1, Math.floor(rect.width));
    const newH = Math.max(1, Math.floor(rect.height));

    // Skip if nothing actually changed (mobile address bar, etc.)
    if (newW === width && newH === height) {
      ticker?.start?.();
      return;
    }

    width = newW;
    height = newH;

    // background is blurred; DPR 2 is usually wasted work
    dpr = 0.3; // render at 30% — blur hides the difference completely

    canvas.width = Math.floor(width * dpr);
    canvas.height = Math.floor(height * dpr);
    canvas.style.width = width + "px";
    canvas.style.height = height + "px";

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    setupLayers();
  }

  function draw(now, dt) {
    ctx.clearRect(0, 0, width, height);
    ribbons?.draw(now, dt);

    if (!readySent) {
      readySent = true;
      onReady?.();
    }
  }

  resize();

  let resizeTimer = 0;
  function requestResize() {
    // stop the ticker during live resize to avoid drawing into a reallocating surface
    ticker?.stop?.();

    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      resize();
      ticker?.start?.();
    }, 200);
  }

  window.addEventListener("resize", requestResize, { passive: true });

  const ticker = createCanvasTicker({
    // observe hero section (more robust than the background layer)
    elementToObserve: hero || waveBg,

    // If you want throttle, set fps < unthrottledFPS (e.g. 40)
    fps: 60,

    onFrame: draw,
  });

  ticker.start();

  return {
    destroy() {
      ticker.destroy();
      window.removeEventListener("resize", resize);
    },
  };
}
